/**
 * Dialog that lets the user toggle dashboard widgets on/off.
 * Widgets are grouped by category.
 */
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Separator,
} from '@erp/ui';
import type { WidgetDef, WidgetCategory } from './registry';
import { Eye, EyeOff, SlidersHorizontal, RotateCcw } from '@/components/icons';

interface PersonalizeDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  allWidgets: WidgetDef[];
  isVisible: (id: string) => boolean;
  toggle: (id: string) => void;
  showAll: () => void;
  hideAll: () => void;
  resetDefaults: () => void;
}

const CATEGORY_ORDER: WidgetCategory[] = [
  'overview',
  'sales',
  'purchases',
  'finance',
  'checks',
  'stock',
  'operations',
  'withholding',
];

export function PersonalizeDialog({
  open,
  onOpenChange,
  allWidgets,
  isVisible,
  toggle,
  showAll,
  hideAll,
  resetDefaults,
}: PersonalizeDialogProps) {
  const { t } = useTranslation();

  // Group by category
  const byCategory = new Map<WidgetCategory, WidgetDef[]>();
  for (const w of allWidgets) {
    if (!byCategory.has(w.category)) byCategory.set(w.category, []);
    byCategory.get(w.category)!.push(w);
  }

  const visibleCount = allWidgets.filter((w) => isVisible(w.id)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal size={18} />
            {t('dashboard.personalize.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between text-sm text-muted-foreground border-y py-2">
          <span>{t('dashboard.personalize.visibleCount', { count: visibleCount, total: allWidgets.length })}</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={showAll} className="h-7 text-xs">
              <Eye size={12} className="me-1" />
              {t('dashboard.personalize.showAll')}
            </Button>
            <Button size="sm" variant="ghost" onClick={hideAll} className="h-7 text-xs">
              <EyeOff size={12} className="me-1" />
              {t('dashboard.personalize.hideAll')}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-1 space-y-4">
          {CATEGORY_ORDER.map((cat) => {
            const widgets = byCategory.get(cat);
            if (!widgets || widgets.length === 0) return null;
            return (
              <div key={cat} className="space-y-1.5">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(`dashboard.personalize.category.${cat}`)}
                </h3>
                <div className="space-y-1">
                  {widgets.map((w) => {
                    const on = isVisible(w.id);
                    return (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => toggle(w.id)}
                        className={`w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm text-start transition-colors hover:bg-accent/50 ${on ? 'border-primary/40 bg-primary/5' : 'border-border'}`}
                      >
                        <span className="flex-1">{t(w.titleKey)}</span>
                        {on ? (
                          <Eye size={14} className="text-primary shrink-0" />
                        ) : (
                          <EyeOff size={14} className="text-muted-foreground shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <Separator />

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" size="sm" onClick={resetDefaults}>
            <RotateCcw size={14} className="me-1" />
            {t('dashboard.personalize.resetDefaults')}
          </Button>
          <Button onClick={() => onOpenChange(false)}>{t('common.close') ?? 'Close'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
